import { useState, useMemo } from "react";
import { ChevronRight, Loader2, FileDown } from "lucide-react";
import { createColumnHelper } from "@tanstack/react-table";
import { Plus, Eye, Trash2, Pencil, ChevronDown, ChevronUp } from "lucide-react";
import { DataTable } from "@/components/ui/virtualizedTable";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Combobox } from "@/components/ui/combobox";
import { Card, CardContent } from "@/components/ui/card";
import { Switch } from "@/components/ui/switch";
import { useListCustomers } from "@/lib/api/customer";
import { CommodityOptions } from "@/lib/api/commodity";
import { Pagination, useListExportSalesTracking, ExportSalesTrackerFilters, SalesExportTracker, SalesExportTrackerContract } from "@/lib/api/exportSalesTracking";
import { safeParseDecimal } from "@/lib/api/common";
import { formatNumberToDisplay } from "@/lib/api/common";
import moment from "moment-timezone";
import { CommonDestinationTimezone } from "@/lib/utils";
import { UploadIngestReportDialog } from "./UploadIngestReportDialog";
import { CreateContractsDialog } from "./CreateContractsDialog";
import { UpdateContractDialog } from "./UpdateContractDialog";
import { DeleteConfirmDialog } from "./DeleteConfirmDialog";
import { generateExportSalesTrackingReport } from "@/lib/exportSalesTrackingReport";

const limit: number = 100000;

export const ExportSalesTrackerDashboard = () => {
    const [isFilterOpen, setIsFilterOpen] = useState(false);
    const [showOnlyUnsigned, setShowOnlyUnsigned] = useState(false);
    const [filters, setFilters] = useState<ExportSalesTrackerFilters>({
        page: 1,
        limit,
    });
    const [createDialogOpen, setCreateDialogOpen] = useState(false);
    const [selectedTrackerId, setSelectedTrackerId] = useState<string | null>(null);
    const [updateDialogOpen, setUpdateDialogOpen] = useState(false);
    const [selectedContract, setSelectedContract] = useState<{
        trackerId: string;
        contractId: string;
        contract_number: string;
        has_been_signed: boolean;
    } | null>(null);
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [deleteInfo, setDeleteInfo] = useState<{
        type: "tracker" | "contract";
        trackerId: string;
        contractId?: string;
        settlementNumber?: string;
        contractNumber?: string;
        toggleExpanded?: () => void;
        contractsCount?: number;
    } | null>(null);

    // Fetch data
    const exportSalesTrackingResult = useListExportSalesTracking(filters);
    const minimalCustomersListResult = useListCustomers({ minimal: true });
    const customers = minimalCustomersListResult?.data?.data || [];
    const customerOptions = customers.map((customer: { id: string; long_name: string }) => ({ value: customer.id, label: customer.long_name }));

    // Check if data is successful
    const isSuccess = !!exportSalesTrackingResult?.data?.success;
    const exportSalesTrackers: SalesExportTracker[] | undefined = isSuccess ? exportSalesTrackingResult?.data?.data?.data : null;
    const pagination: Pagination | undefined = isSuccess ? exportSalesTrackingResult?.data?.data?.pagination : null;

    // Transform data for table with proper grouping structure
    const tableData = useMemo(() => {
        if (!exportSalesTrackers) return [];

        // Group by settlement number (grouped_id) and flatten for table
        const grouped = exportSalesTrackers.reduce((acc: Record<string, { tracker_id: string; contracts_count: number; signed_count: number; has_contracts: boolean; parent: SalesExportTracker; grouped_id: string; items: SalesExportTrackerContract[]; }>, tracker: SalesExportTracker) => {
            if (!acc[tracker.grouped_id]) {
                acc[tracker.grouped_id] = {
                    parent: tracker,
                    tracker_id: tracker.id,
                    grouped_id: tracker.grouped_id,
                    items: !tracker.Contracts?.length ? ([tracker as any]) : tracker.Contracts || [],
                    has_contracts: !!tracker.Contracts?.length,
                    signed_count: tracker.Contracts?.filter((contract: { has_been_signed: boolean }) => contract.has_been_signed)?.length ?? 0,
                    contracts_count: tracker.Contracts?.length ?? 0,
                };
                return acc;
            }

            return acc;
        }, {} as Record<string, { tracker_id: string; grouped_id: string; items: SalesExportTrackerContract[]; parent: SalesExportTracker, has_contracts: boolean; signed_count: number; contracts_count: number; }>);

        // Flatten for table structure
        const flattened: any[] = [];
        Object.values(grouped).forEach((group) => {
            group.items.forEach((item: any) => {
                flattened.push({
                    ...group.parent,
                    ...item,
                    grouped_id: group.parent.grouped_id,
                    id: `${group.tracker_id}_${item.id}`,
                    tracker_id: group.tracker_id,
                    tracker_contract_id: item.id,
                    has_contracts: group.has_contracts,
                    signed_count: group.signed_count,
                    contracts_count: group.contracts_count,
                });
            });
        });

        if (showOnlyUnsigned) {
            return flattened.filter((item) => {
                return item.contracts_count > item.signed_count;
            });
        }

        return flattened;
    }, [exportSalesTrackers, showOnlyUnsigned]);

    const handleAdd = (options: { trackerId: string }) => () => {
        setSelectedTrackerId(options.trackerId);
        setCreateDialogOpen(true);
    };

    const handleEdit = (row: any) => () => {
        setSelectedContract({
            trackerId: row.tracker_id,
            contractId: row.tracker_contract_id,
            contract_number: row.contract_number || "",
            has_been_signed: row.has_been_signed || false
        });
        setUpdateDialogOpen(true);
    };

    const handleDelete = (row: any, isAggregated: boolean = false, toggleExpanded?: () => void, contractsCount?: number) => () => {
        if (isAggregated) {
            // Delete entire tracker/settlement
            setDeleteInfo({
                type: "tracker",
                trackerId: row.tracker_id,
                settlementNumber: row.grouped_id,
                toggleExpanded
            });
        } else {
            // Delete individual contract
            setDeleteInfo({
                type: "contract",
                trackerId: row.tracker_id,
                contractId: row.tracker_contract_id,
                contractNumber: row.contract_number,
                toggleExpanded,
                contractsCount
            });
        }
        setDeleteDialogOpen(true);
    };

    // Column definitions
    const columnHelper = createColumnHelper<any>();
    const columns = useMemo(() => [
        columnHelper.accessor("id", {
            id: 'id',
            header: "ID",
        }),
        columnHelper.accessor("grouped_id", {
            id: "grouped_id",
            header: "Settlement #",
            enableGrouping: true,
            cell: (info) => {
                if (!info.row.original.has_contracts) {
                    return (<span className="font-medium">{info.getValue()}</span>);
                }
                if (info.row.getIsGrouped()) {
                    return (
                        <div className="flex items-center gap-2">
                            <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => info.row.toggleExpanded()}
                                className="p-0 h-auto"
                            >
                                {info.row.getIsExpanded() ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                            </Button>
                            <span className="font-medium">{info.getValue()}</span>
                            <span className="text-muted-foreground">({info.row.subRows.length} items)</span>
                        </div>
                    );
                }
                return <span className="ml-2 font-bold">{info.row.original.contract_number || ""}</span>;
            },
        }),
        columnHelper.accessor("Customer.long_name", {
            id: "customer_name",
            header: "Customer",
            aggregatedCell: ({ row }: any) => {
                return row.original?.Customer?.long_name || ""
            },
            cell: (info) => {

                return null;
            },
        }),
        columnHelper.accessor("commodity", {
            id: "commodity",
            header: "Commodity",
            ...({ accessorFn: (row: any) => {
                const commodityId = row?.commodity || "";
                return CommodityOptions.find(option => option.value === commodityId)?.label || commodityId;
            }}),
            aggregatedCell: ({ row }: any) => {
                const commodityId = row?.original?.commodity || "";
                return CommodityOptions.find(option => option.value === commodityId)?.label || commodityId;
            },
            cell: (info) => {
                const commodityId = info.row.original?.commodity || "";
                return null;
            },
        }),
        columnHelper.accessor("amount", {
            id: "amount",
            header: "Amount",
            cell: (info) => {
                return null;
            },
            aggregatedCell: (info) => {
                const amount = formatNumberToDisplay(info.row.original.amount, {  });
                return <div className="text-right">${amount}</div>;
            },
        }),
        columnHelper.accessor("grouped_date", {
            id: "grouped_date",
            header: "Date",
            cell: (info) => {
                return null;
            },
            aggregatedCell: (info) => {
                const date = info.row.original.grouped_date;
                return date ? moment.tz(date, CommonDestinationTimezone).format("MM/DD/YYYY") : "-";
            }
        }),
        columnHelper.accessor("has_been_signed", {
            id: "has_been_signed",
            header: "Has Been Signed",
            enableColumnFilter: false,
            aggregatedCell: ({ row }: any) => row.original.contracts_count === 0 ? '-' : (<span>{row.original.signed_count}/{row.original.contracts_count} Signed</span>),
            cell: (info) => {
                return info.row.original.has_been_signed ? "Yes" : "No";
            },
        }),
        columnHelper.display({
            id: "actions",
            header: "Actions",
            aggregatedCell: ({ row }: any) =>{
                return (
                    <div className="flex gap-1">
                        <Button variant="outline" size="icon" className="h-8 w-8" onClick={handleAdd({ trackerId: row.original.tracker_id })}>
                            <Plus className="h-4 w-4" />
                        </Button>
                        <Button variant="outline" size="icon" className="h-8 w-8" onClick={handleDelete(row.original, true, row.toggleExpanded)}>
                            <Trash2 className="h-4 w-4" />
                        </Button>
                    </div>
                );
            },
            cell: ({ row }) => {
                const parentRow = row.getParentRow();
                const contractsCount = parentRow?.subRows?.length || 0;
                return (
                    <div className="flex gap-1">
                        <Button variant="outline" size="icon" className="h-8 w-8" onClick={handleEdit(row.original)}>
                            <Pencil className="h-4 w-4" />
                        </Button>
                        <Button variant="outline" size="icon" className="h-8 w-8" onClick={handleDelete(row.original, false, parentRow?.toggleExpanded, contractsCount)}>
                            <Trash2 className="h-4 w-4" />
                        </Button>
                    </div>
                );
            },
        }),
    ], []);

    // Handle filter changes
    const handleFilterChange = (field: keyof ExportSalesTrackerFilters, value: any) => {
        setFilters(prev => ({
            ...prev,
            [field]: value,
            page: 1, // Reset to first page on filter change
        }));
    };

    // Handle customer selection
    const handleCustomerChange = (customerId: string) => {
        handleFilterChange("customerId", customerId);
    };

    // Pagination handlers
    const handlePageChange = (newPage: number) => {
        setFilters(prev => ({ ...prev, page: newPage }));
    };

    const totalPages = pagination?.totalPages || 1;
    const currentPage = pagination?.page || 1;

    // Handle report generation
    const handleGenerateReport = async () => {
        if (exportSalesTrackers && exportSalesTrackers.length > 0) {
            await generateExportSalesTrackingReport(exportSalesTrackers, { filterToOnlyUnsigned: showOnlyUnsigned });
        }
    };

    return (
        <section className="flex min-h-full flex-1 flex-col justify-start px-6 py-6 lg:px-8">
            <div className="mx-auto w-full">
                <h1 className="scroll-m-20 text-2xl font-extrabold tracking-tight lg:text-3xl mb-6">
                    Export Sales Tracker
                </h1>

                {/* Collapsible Filters */}
                <Card className="mb-6">
                    <div
                        className="flex items-center justify-between p-4 cursor-pointer"
                        onClick={() => setIsFilterOpen(!isFilterOpen)}
                    >
                        <span className="flex flex-row gap-2 items-center">
                            <h2 className="text-lg font-semibold">Filters</h2>
                            <span className="text-muted-foreground text-xs flex flex-row gap-2 items-center">{exportSalesTrackingResult.isLoading ? <Loader2 className="h-3 w-3 animate-spin" /> : ""}{exportSalesTrackingResult.isLoading ? "Loading..." : ""}</span>
                        </span>
                        <div className="mt-4 flex justify-end">
                        </div>
                        <span className="flex flex-row flex-end gap-2">
                            <Button
                                variant="default"
                                onClick={handleGenerateReport}
                                disabled={exportSalesTrackingResult.isLoading || !exportSalesTrackers?.length}
                            >
                                <FileDown className="h-4 w-4 mr-2" />
                                Generate Report
                            </Button>
                            <UploadIngestReportDialog />
                            <Button
                                variant="outline"
                                onClick={() => {
                                    setFilters({ page: 1, limit });
                                    setShowOnlyUnsigned(false);
                                }}
                            >
                                Clear Filters
                            </Button>
                            <Button variant="ghost" size="icon" className="h-8 w-8">
                                {isFilterOpen ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                            </Button>
                        </span>
                    </div>

                    {isFilterOpen && (
                        <CardContent className="pt-0 pb-4">
                            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">

                                {/* Customer */}
                                <div className="grid">
                                    <Label htmlFor="customer">Customer</Label>
                                    <Combobox
                                        options={customerOptions}
                                        value={filters.customerId || ""}
                                        onValueChange={(customerId: string) => handleCustomerChange(customerId)}
                                        placeholder="Select customer"
                                        dwimOnChange
                                        isModal={false}
                                        className="w-full"
                                        popoverClassName="min-w-[400px]"
                                    />
                                </div>

                                {/* Commodity */}
                                <div className="grid">
                                    <Label htmlFor="commodity">Commodity</Label>
                                    <Combobox
                                        options={CommodityOptions}
                                        value={filters.commodity || ""}
                                        onValueChange={(commodity: string) => handleFilterChange("commodity", commodity)}
                                        placeholder="Select commodity"
                                        dwimOnChange
                                        isModal={false}
                                        className="w-full"
                                        popoverClassName="min-w-[200px]"
                                    />
                                </div>

                                {/* Grouped Date From */}
                                <div className="grid">
                                    <Label htmlFor="groupedFrom">Grouped From</Label>
                                    <Input
                                        id="groupedFrom"
                                        type="date"
                                        value={filters.groupedDateFrom || ""}
                                        onChange={(e) => handleFilterChange("groupedDateFrom", e.target.value || undefined)}
                                    />
                                </div>

                                {/* Grouped Date To */}
                                <div className="grid">
                                    <Label htmlFor="groupedTo">Grouped To</Label>
                                    <Input
                                        id="groupedTo"
                                        type="date"
                                        value={filters.groupedDateTo || ""}
                                        onChange={(e) => handleFilterChange("groupedDateTo", e.target.value || undefined)}
                                    />
                                </div>
                            </div>

                            {/* Toggle for unsigned contracts */}
                            <div className="flex items-center space-x-2 mt-4">
                                <Switch
                                    id="showOnlyUnsigned"
                                    checked={showOnlyUnsigned}
                                    onCheckedChange={setShowOnlyUnsigned}
                                />
                                <Label
                                    htmlFor="showOnlyUnsigned"
                                    className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
                                >
                                    Only show trackers with unsigned contracts
                                </Label>
                            </div>

                        </CardContent>
                    )}
                </Card>

                {/* Data Table */}
                {exportSalesTrackingResult.error || (!exportSalesTrackingResult.isLoading && !isSuccess) ? (
                    <div className="text-center py-8 text-red-500">
                        Error loading data: {exportSalesTrackingResult.error instanceof Error ? exportSalesTrackingResult.error.message : exportSalesTrackingResult?.data?.message || "Unknown error"}
                    </div>
                ) : (
                    <>
                        <DataTable
                            columnVisibility={{ id: false }}
                            columns={columns as any[]}
                            data={tableData}
                            height="600px"
                            defaultGrouping={["grouped_id"]}
                            defaultSorting={[{ id: "grouped_id", desc: true }]}
                            emptyResultsMessage={exportSalesTrackingResult.isLoading ? "Loading..." : "No export sales tracking data found."}
                            useRowUuid
                        />

                        {/* Pagination Controls */}
                        {!exportSalesTrackingResult.isLoading && (
                            <div className="flex items-center justify-between mt-4">
                                <div className="text-sm text-muted-foreground">
                                    Page {currentPage} of {totalPages} | Total: {pagination?.total || 0} records
                                </div>
                                <div className="flex gap-2">
                                    <Button
                                        variant="outline"
                                        size="sm"
                                        onClick={() => handlePageChange(currentPage - 1)}
                                        disabled={currentPage <= 1}
                                    >
                                        Previous
                                    </Button>
                                    <Button
                                        variant="outline"
                                        size="sm"
                                        onClick={() => handlePageChange(currentPage + 1)}
                                        disabled={currentPage >= totalPages}
                                    >
                                        Next
                                    </Button>
                                </div>
                            </div>
                        )}
                    </>
                )}
            </div>

            {/* Create Contracts Dialog */}
            {selectedTrackerId && (
                <CreateContractsDialog
                    trackerId={selectedTrackerId}
                    open={createDialogOpen}
                    onOpenChange={setCreateDialogOpen}
                    onSuccess={() => {}}
                />
            )}

            {/* Update Contract Dialog */}
            {selectedContract && (
                <UpdateContractDialog
                    trackerId={selectedContract.trackerId}
                    contractId={selectedContract.contractId}
                    initialData={{
                        contract_number: selectedContract.contract_number,
                        has_been_signed: selectedContract.has_been_signed
                    }}
                    open={updateDialogOpen}
                    onOpenChange={setUpdateDialogOpen}
                    onSuccess={() => {}}
                />
            )}

            {/* Delete Confirm Dialog */}
            {deleteInfo && (
                <DeleteConfirmDialog
                    type={deleteInfo.type}
                    trackerId={deleteInfo.trackerId}
                    contractId={deleteInfo.contractId}
                    settlementNumber={deleteInfo.settlementNumber}
                    contractNumber={deleteInfo.contractNumber}
                    open={deleteDialogOpen}
                    onOpenChange={setDeleteDialogOpen}
                    onSuccess={() => {
                        // Only collapse the row if this was the last contract
                        if (deleteInfo?.type === 'contract' && deleteInfo?.contractsCount === 1 && deleteInfo?.toggleExpanded) {
                            deleteInfo.toggleExpanded();
                        }
                        // Always collapse for tracker deletion
                        if (deleteInfo?.type === 'tracker' && deleteInfo?.toggleExpanded) {
                            deleteInfo.toggleExpanded();
                        }
                    }}
                />
            )}
        </section>
    );
};
